import _ from 'lodash'
import React, { CSSProperties } from 'react'
import classNames from 'classnames'

import apis from 'browser/app/models/apis'
import { getWebEntityDisplay, getWebEntityIconByName } from 'browser/app/models/entity-display-registry'
import { Settings } from 'browser/app/models/settings'
import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import { StatusBadge } from 'browser/components/atomic-elements/atoms/status-badge/status-badge'
import { EntityFormModal } from 'browser/components/atomic-elements/organisms/entity/entity-form-modal'
import { EntityFormSheetMode } from 'browser/components/atomic-elements/organisms/entity/entity-form-sheet'
import { UI2PlatformContext } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { EntityPreviewCardContext } from 'shared-libs/components/ui2/display/entity-preview-card/entity-preview-card-context'
import {
  EntityPreviewCardCellProps,
  EntityPreviewCardCellType,
  EntityPreviewCardProps,
} from 'shared-libs/components/ui2/display/entity-preview-card/entity-preview-card-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { AsyncFormulaResolver } from 'shared-libs/helpers/async-formula-resolver'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { objectToString } from 'shared-libs/helpers/formulas/modules'
import { Entity } from 'shared-libs/models/entity'

import 'browser/components/ui2/display/ui2-entity-preview-card/_ui2-entity-preview-card.scss'

/**
 * Interaction matrix for the click/tap handler.
 *
 * | isDisabled | isReadOnly | entityReadOnlySchemaPath | entityEditSchemaPath | Viewer's firm owns entity? | Result                                             |
 * |------------|------------|--------------------------|----------------------|----------------------------|----------------------------------------------------|
 * | true       | any        | any                      | any                  | any                        | Muted card, no-op                                  |
 * | false      | true       | resolved                 | any                  | any                        | Interactive card, opens read-only sheet            |
 * | false      | true       | unresolved               | any                  | any                        | Muted card, no-op                                  |
 * | false      | false      | any                      | resolved             | yes                        | Interactive card, opens edit sheet                 |
 * | false      | false      | any                      | unresolved           | yes (DW)                   | Interactive card, opens `/entity/<id>` in a new tab |
 * | false      | false      | resolved                 | unresolved           | yes (MW)                   | Interactive card, opens read-only sheet            |
 * | false      | false      | unresolved               | unresolved           | yes (MW)                   | Muted card, no-op                                  |
 * | false      | false      | resolved                 | any                  | no                         | Interactive card, opens read-only sheet            |
 * | false      | false      | unresolved               | any                  | no                         | Muted card, no-op                                  |
 *
 * Ownership: `entity.owner.firm.entityId === apis.getSettings().firm.uniqueId`.
 * "Resolved" means `entity.resolveSubschemaByPath(path)` returns a schema.
 *
 * The new-tab fallback for an unresolved edit path is DW-only. MW has no
 * second-tab affordance, so there the edit action is simply disabled and the
 * card falls through to read-only (or renders muted). NM matches MW.
 */

type ClickResolution = 'edit' | 'readonly' | 'none'

export interface IUI2EntityPreviewCardProps extends IBaseProps, EntityPreviewCardProps {
  frames?: FramesManager
  style?: CSSProperties
  /**
   * Component registry passed to `EntityFormModal` when the card opens
   * an entity for edit / view. Defaults to the DW `browser/components`
   * map inside the modal. MW wrappers pass `MobileComponents` so the
   * modal renders MW-native fields when opened from a mobile-web surface.
   */
  componentsMap?: Record<string, any>
  /**
   * Class applied to the `EntityFormModal` root when this card's click
   * opens one — used by MW wrappers to select the MW variant
   * (`c-entityFormModal--mobileWeb`).
   */
  entityFormModalClassName?: string
  /** Called when any entity form modal opened by this card is closed. */
  onModalClose?: () => void
}

interface IUI2EntityPreviewCardState {
  entity: Entity
  isLoading: boolean
}

/**
 * @uiComponent ui2:entityPreviewCard
 *
 * UI2EntityPreviewCard — Card component showing a formatted summary of an
 * entity. Clicking the card resolves via the interaction matrix above to
 * open the entity's edit sheet, read-only sheet, or nothing at all — no
 * custom click handlers are supported.
 */
export class UI2EntityPreviewCard extends React.PureComponent<
  IUI2EntityPreviewCardProps,
  IUI2EntityPreviewCardState
> {
  public static contextType = UI2PlatformContext
  declare context: React.ContextType<typeof UI2PlatformContext>

  private store: any
  private settings: Settings
  private formulaResolver = new AsyncFormulaResolver(() => this.forceUpdate())

  public static defaultProps: Partial<IUI2EntityPreviewCardProps> = {
    entityEditSchemaPath: 'uiSchema.shared.entityEditSchema',
    showEntityType: false,
  }

  constructor(props: IUI2EntityPreviewCardProps) {
    super(props)
    this.store = apis.getStore()
    this.state = {
      entity: null,
      isLoading: false,
    }
    this.settings = apis.getSettings()
  }

  public componentWillUnmount() {
    this.formulaResolver.dispose()
  }

  public componentDidMount() {
    const { entity, value } = this.props
    if (entity?.entityId || value?.entityId) {
      this.fetchValueEntity(value?.entityId ?? entity?.entityId)
    } else if (entity) {
      this.setState({ entity, isLoading: false })
    }
  }

  public componentDidUpdate(prevProps: IUI2EntityPreviewCardProps) {
    const { entity, value } = this.props
    const entityId: string = value?.entityId ?? entity?.entityId ?? entity?.uniqueId
    const prevEntityId: string =
      prevProps?.value?.entityId ?? prevProps?.entity?.entityId ?? prevProps?.entity?.uniqueId

    if (entityId !== prevEntityId) {
      if (_.isNil(entityId)) {
        this.setState({ entity: undefined, isLoading: false })
      } else if (entity?.entityId || value?.entityId) {
        this.fetchValueEntity(value?.entityId ?? entity?.entityId)
      } else {
        this.setState({ entity, isLoading: false })
      }
    }
  }

  public render() {
    if (this.props.isChipMode) {
      return (
        <EntityPreviewCardContext.Consumer>
          {(cardCtx) => this.renderChipMode(cardCtx.showTypeStripe)}
        </EntityPreviewCardContext.Consumer>
      )
    }

    if (this.state.isLoading) {
      return (
        <div className="pa3 pt4 bt b--light-gray">
          <LoadingSpinner size="xs" />
        </div>
      )
    }

    return (
      <EntityPreviewCardContext.Consumer>
        {(cardCtx) => this.renderContent(cardCtx.showTypeStripe)}
      </EntityPreviewCardContext.Consumer>
    )
  }

  private renderChipMode(contextShowTypeStripe: boolean) {
    const { className, style, title, showTypeStripe: propShowTypeStripe, typeStripeIconName } = this.props
    const { entity } = this.state

    // Chip mode defaults to showing the type stripe
    const showTypeStripe = propShowTypeStripe ?? contextShowTypeStripe ?? true
    const propIconName = showTypeStripe ? getWebEntityIconByName(typeStripeIconName) : undefined
    const schemaDisplay = (showTypeStripe && !propIconName) ? getWebEntityDisplay(entity) : undefined
    const stripeIconName = propIconName ?? schemaDisplay?.iconName

    const displayName = title ?? entity?.displayName ?? ''

    const resolution = this.resolveClick()
    const isInteractive = resolution !== 'none'

    return (
      <div
        className={classNames(
          'ui2-entity-preview-card',
          'ui2-entity-preview-card--isChip',
          {
            'ui2-entity-preview-card--isInteractive': isInteractive,
            'ui2-entity-preview-card--isMuted': !isInteractive,
          },
          className
        )}
        style={style}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onTouchStart={isInteractive ? (e) => this.handleOnClick(e, resolution) : undefined}
        onClick={isInteractive ? (e) => this.handleOnClick(e, resolution) : undefined}
        onKeyDown={isInteractive ? (e) => { if (e.key === 'Enter') this.handleOnClick(e, resolution) } : undefined}
      >
        {stripeIconName && (
          <div className='ui2-entity-preview-card__typeStripe'>
            <UI2Icon name={stripeIconName} color='var(--ui2-espc-stripe-icon-color)' size={14} />
          </div>
        )}
        <div className='ui2-entity-preview-card__chipName'>
          {displayName}
        </div>
      </div>
    )
  }

  private renderContent(contextShowTypeStripe: boolean) {
    const { className, style } = this.props

    const resolution = this.resolveClick()
    const isInteractive = resolution !== 'none'
    const isMuted = !isInteractive

    const showTypeStripe = this.props.showTypeStripe ?? contextShowTypeStripe
    const { typeStripeIconName } = this.props
    const propIconName = showTypeStripe ? getWebEntityIconByName(typeStripeIconName) : undefined
    // Only the icon is read off the schema display — the stripe no longer
    // colour-codes by entity type, so `schemaDisplay.color` goes unused.
    const schemaDisplay = (showTypeStripe && !propIconName)
      ? getWebEntityDisplay(this.state.entity)
      : undefined
    const stripeIconName = propIconName ?? schemaDisplay?.iconName

    if (!this.state.entity) return null
    return (
      <div
        className={classNames(
          'ui2-entity-preview-card',
          { 'ui2-entity-preview-card--isInteractive': isInteractive },
          { 'ui2-entity-preview-card--isMuted': isMuted },
          className
        )}
        style={style}
        role={isInteractive ? 'button' : undefined}
        tabIndex={0}
        onTouchStart={isInteractive ? (e) => this.handleOnClick(e, resolution) : undefined}
        onClick={isInteractive ? (e) => this.handleOnClick(e, resolution) : undefined}
        onKeyDown={(e) => {
          if (!isInteractive) return
          if (e.key === 'Enter') this.handleOnClick(e, resolution)
        }}
      >
        {stripeIconName && (
          <div className='ui2-entity-preview-card__typeStripe'>
            <UI2Icon name={stripeIconName} color='var(--ui2-espc-stripe-icon-color)' size={18} />
          </div>
        )}
        <div className="ui2-entity-preview-card__body">
          <div className="ui2-entity-preview-card__headerRow">
            <div className="ui2-entity-preview-card__headerLeft">
              {this.renderTitleRow()}
              {this.renderIdentifierRow(this.getCells([EntityPreviewCardCellType.IDENTIFIER]))}
            </div>
            <div className="ui2-entity-preview-card__headerRight">
              {this.renderStatusRow(this.getCells([EntityPreviewCardCellType.STATUS]))}
            </div>
          </div>
          {this.renderInfoRow(
            this.getCells([
              EntityPreviewCardCellType.INFO,
              EntityPreviewCardCellType.INFO_FROM_ARRAY,
              EntityPreviewCardCellType.NOTES,
            ])
          )}
        </div>
      </div>
    )
  }

  private renderTitleRow() {
    const { entity } = this.state

    const title = this.props.title ?? entity?.displayName
    if (!title) {
      return null
    }
    return (
      <>
        {this.props.showEntityType && entity?.entityType && (
          <div className="ui2-entity-preview-card__label mb1">{entity.entityType}</div>
        )}
        <div className="ui2-entity-preview-card__title">{String(title)}</div>
      </>
    )
  }

  private renderIdentifierRow(filteredCells: EntityPreviewCardCellProps[]) {
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="ui2-entity-preview-card__identifiers">
        {filteredCells.map((cell, i) => (
          <div key={i}>{cell.value}</div>
        ))}
      </div>
    )
  }

  private renderInfoRow(cells: EntityPreviewCardCellProps[]) {
    const filteredCells = _.sortBy(cells, ['type'])
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="ui2-entity-preview-card__info">
        {filteredCells.map((cell, index) => (
          <div
            key={index}
            className={classNames('ui2-entity-preview-card__cell', {
              'ui2-entity-preview-card__cell--notes':
                cell.type === EntityPreviewCardCellType.NOTES,
            })}
          >
            {cell.label && <div className="ui2-entity-preview-card__label">{cell.label}</div>}
            <div className="ui2-entity-preview-card__cell-value">{cell.value}</div>
          </div>
        ))}
      </div>
    )
  }

  private renderStatusRow(filteredCells: EntityPreviewCardCellProps[]) {
    if (filteredCells.length === 0) {
      return null
    }
    return (
      <div className="ui2-entity-preview-card__status">
        {filteredCells.map((cell, index) => (
          <StatusBadge key={index} text={cell.value} color={cell.color} />
        ))}
      </div>
    )
  }

  private getCells(types: EntityPreviewCardCellType[]): EntityPreviewCardCellProps[] {
    const { data } = this.props
    const { entity } = this.state

    const customIdentifierCells: EntityPreviewCardCellProps[] = _.compact(
      _.flatMap(data || [], (cell) => {
        if (cell.type === EntityPreviewCardCellType.INFO_FROM_ARRAY) {
          return this.buildCustomIdentifierCells(entity, cell)
        }
      })
    )

    return [...(data || []), ...(customIdentifierCells || [])]
      .map((cell) => this.getCellWithValue(cell, entity))
      .filter((cell) => {
        const exists = !_.isEmpty(cell) && !_.isNil(cell)
        const hasValue = !_.isNil(cell.value) && !_.isEmpty(cell.value)
        return (
          exists &&
          hasValue &&
          _.includes(types, cell.type) &&
          cell.type !== EntityPreviewCardCellType.INFO_FROM_ARRAY
        )
      })
  }

  private getCellWithValue(cell: EntityPreviewCardCellProps, entity: Entity) {
    if (cell.path) {
      return { ...cell, value: objectToString(_.get(entity, cell.path, '')) }
    } else if (cell.formula) {
      return { value: this.evaluateCellFormula(cell.formula, entity), ...cell }
    }
    return cell
  }

  private evaluateCellFormula(formula: string, entity: Entity) {
    return this.formulaResolver.resolve(`${entity?.uniqueId}::${formula}`, () =>
      evaluateExpressionWithScopes(this.props.frames, formula, {
        settings: this.settings,
        ...CustomFormulas,
        ...entity,
      })
    )
  }

  private buildCustomIdentifierCells(entity: Entity, cell: EntityPreviewCardCellProps) {
    const results: EntityPreviewCardCellProps[] = []
    let customIdentifierValues: any
    if (cell.path) {
      customIdentifierValues = _.get(entity, cell.path)
    } else if (cell.formula) {
      customIdentifierValues = this.evaluateCellFormula(cell.formula, entity)
    }
    const groupedIdentifiers = this.groupCustomIdentifiers(
      customIdentifierValues,
      cell.labelPath,
      cell.valuePath
    )
    for (const val of groupedIdentifiers || []) {
      results.push({
        label: val.label,
        value: objectToString(val.value),
        type: EntityPreviewCardCellType.INFO,
      })
    }
    return results
  }

  private groupCustomIdentifiers(
    identifierArray: any[],
    labelPath: string = 'label',
    valuePath: string = 'value'
  ): any[] {
    if (!Array.isArray(identifierArray)) {
      return []
    }
    const grouped = _.groupBy(identifierArray, (item) => item[labelPath])
    return _.map(grouped, (items, key) => {
      const values = _.map(items, (item) => item[valuePath])
      return { label: key, value: values.join(', ') }
    })
  }

  private resolveClick(): ClickResolution {
    const { isDisabled, isReadOnly, entityEditSchemaPath, entityReadOnlySchemaPath } = this.props
    const { entity } = this.state

    if (isDisabled || !entity) return 'none'

    const readonlyResolves =
      !!entityReadOnlySchemaPath && !!entity.resolveSubschemaByPath?.(entityReadOnlySchemaPath)

    if (isReadOnly) {
      return readonlyResolves ? 'readonly' : 'none'
    }

    if (this.viewerOwnsEntity(entity)) {
      const editResolves =
        !!entityEditSchemaPath && !!entity.resolveSubschemaByPath?.(entityEditSchemaPath)
      // DW keeps the new-tab fallback for an unresolved edit path, so the
      // click still resolves to 'edit'. MW has no new-tab affordance: with
      // nothing to open, the edit action is disabled and the card falls
      // through to read-only or renders muted.
      if (editResolves || this.context !== 'mobileWeb') return 'edit'
    }
    return readonlyResolves ? 'readonly' : 'none'
  }

  private viewerOwnsEntity(entity: Entity): boolean {
    const viewerFirmId = this.settings?.firm?.uniqueId
    const ownerFirmId = _.get(entity, 'owner.firm.entityId')
    if (!viewerFirmId || !ownerFirmId) {
      return true
    }
    return viewerFirmId === ownerFirmId
  }

  private handleOnClick = (event: any, resolution: ClickResolution) => {
    const { entityEditSchemaPath, entityReadOnlySchemaPath } = this.props
    const { entity } = this.state

    event.preventDefault()
    event.stopPropagation()

    if (resolution === 'readonly') {
      const subschema = entity.resolveSubschemaByPath(entityReadOnlySchemaPath)
      if (subschema) {
        this.openEntityFormModal(entityReadOnlySchemaPath, EntityFormSheetMode.View)
      }
      return
    }

    if (resolution === 'edit') {
      const subschema = entityEditSchemaPath && entity.resolveSubschemaByPath(entityEditSchemaPath)
      if (subschema) {
        this.openEntityFormModal(entityEditSchemaPath, EntityFormSheetMode.Edit)
      } else {
        window.open(`/entity/${entity.uniqueId}`, '_blank')
      }
    }
  }

  private openEntityFormModal = (uiSchemaPath: string, mode: EntityFormSheetMode) => {
    const { entity } = this.state
    const isMobileWeb = this.context === 'mobileWeb'
    OverlayManager.openOverlayElement(
      <Modal
        backdropClosesModal={true}
        modalDialogClassName={classNames('c-entityFormModal-dialog', {
          'c-entityFormModal-dialog--mobileWeb': isMobileWeb,
        })}
        modalPaperClassName={classNames('c-entityFormModal-paper', {
          'c-entityFormModal-paper--mobileWeb': isMobileWeb,
        })}
        onClose={this.props.onModalClose}
      >
        <EntityFormModal
          entity={entity}
          entitySchema={entity?.schema}
          uiSchemaPath={uiSchemaPath}
          mode={mode}
          isMobileWeb={isMobileWeb}
          extraState={this.props.extraState}
          componentsMap={this.props.componentsMap}
          className={classNames(this.props.entityFormModalClassName, {
            'c-entityFormModal--mobileWeb': isMobileWeb,
          })}
        />
      </Modal>
    )
  }

  private fetchValueEntity(uniqueId: string) {
    this.setState({ isLoading: true })
    this.store.findRecordThrottled(uniqueId).then((entity: Entity) => {
      this.setState({ entity, isLoading: false })
    })
  }
}
